<template>
  <v-container class="mt-5">
    <v-row no-gutters justify="end">
      <v-select
        :value="preSelectedGraphType"
        class="trending-graph-select"
        :items="graphTypes"
        label="Graph Type"
        @change="onChange"
      />
    </v-row>
    <v-row no-gutters>
      <v-col cols="12">
        <app-graph
          :traces="traces"
          :default-range-size="monthsInYear"
          :title="selectedTrialName"
          :graph-type="preSelectedGraphType"
          @restyle="onRestyle"
        />
      </v-col>
    </v-row>
  </v-container>
</template>

<script>
import _ from 'lodash'
import AppGraph from '@/components/common/AppGraph'
import { getXAxis, GraphTypes, getBaseTrace, TraceTypes } from '@/helpers/graph-helper'
import trialSummaryService from '@/services/trial-summary-service'
import { vuetifyColors } from '@/assets/vuetify-config'
import { MyActiveOrganization } from '@/helpers/my-account-helper'
import { MONTHS_IN_YEAR } from '@/helpers/date-helper'
import { summaryValues } from '@/helpers/summary-value-helper'

export default {
  components: {
    AppGraph
  },
  props: {
    editedSite: { type: Object, default: null },
    orgTrialExternalId: { type: String, default: null },
    trialId: { type: String, default: null },
    trialExternalId: { type: String, default: null },
    selectedTrialName: { type: String, default: null },
    includeSiteInTitle: { type: Boolean, default: false },
    preSelectedGraphType: { type: String, default: GraphTypes.STACKED_AREA },
    selectedTable: { type: String, required: true },
    preSelectedTraces: { type: Array, default: () => [] }
  },
  data() {
    return {
      loading: true,
      trends: [],
      graphTypes: [
        GraphTypes.STACKED_AREA,
        GraphTypes.GROUPED_BAR,
        GraphTypes.STACKED_BAR,
        GraphTypes.LINE
      ]
    }
  },
  computed: {
    monthsInYear() {
      return MONTHS_IN_YEAR
    },
    orgId() {
      return MyActiveOrganization.id
    },
    sortedTrends() {
      return _.sortBy(this.trends, 'trendDate')
    },
    x() {
      return getXAxis(this.sortedTrends)
    },
    awaitingReviewTrace() {
      return this.getTraceFromTrends(
        this.sortedTrends,
        TraceTypes.AWAITING_REVIEW,
        'Awaiting Review',
        vuetifyColors.mediumTwo
      )
    },
    inReviewTrace() {
      return this.getTraceFromTrends(
        this.sortedTrends,
        TraceTypes.IN_REVIEW,
        'In Review',
        vuetifyColors.info
      )
    },
    notEligibleTrace() {
      return this.getTraceFromTrends(
        this.sortedTrends,
        TraceTypes.NOT_ELIGIBLE,
        'Not Eligible',
        vuetifyColors.error
      )
    },
    followingTrace() {
      return this.getTraceFromTrends(
        this.sortedTrends,
        TraceTypes.FOLLOWING,
        'Following',
        vuetifyColors.contingentColor
      )
    },
    matchedTrace() {
      return this.getTraceFromTrends(
        this.sortedTrends,
        TraceTypes.MATCHED,
        'Matched',
        vuetifyColors.matchedColor
      )
    },
    notConsentedTrace() {
      return this.getTraceFromTrends(
        this.sortedTrends,
        TraceTypes.NOT_CONSENTED,
        'Not Consented',
        vuetifyColors.warning
      )
    },
    enrolledTrace() {
      return this.getTraceFromTrends(
        this.sortedTrends,
        TraceTypes.ENROLLED,
        'Enrolled',
        vuetifyColors.success
      )
    },
    traces() {
      return [
        this.awaitingReviewTrace,
        this.inReviewTrace,
        this.notEligibleTrace,
        this.followingTrace,
        this.matchedTrace,
        this.notConsentedTrace,
        this.enrolledTrace
      ]
    },
    trendRequestParams() {
      return {
        trialId: this.trialId,
        summaryValues: [
          summaryValues.PATIENT_AWAITING_REVIEW_COUNT,
          summaryValues.PATIENT_IN_REVIEW_COUNT,
          summaryValues.PATIENT_NOT_ELIGIBLE_COUNT,
          summaryValues.PATIENT_FOLLOWING_COUNT,
          summaryValues.PATIENT_MATCHED_COUNT,
          summaryValues.PATIENT_NOT_CONSENTED_COUNT,
          summaryValues.PATIENT_ENROLLED_COUNT,
          summaryValues.TREND_DATE,
          summaryValues.TRIAL_FRIENDLY_NAME,
          summaryValues.TRIAL_ID,
          summaryValues.ORGANIZATION_EXTERNAL_ID,
          summaryValues.ORGANIZATION_NAME
        ]
      }
    }
  },
  watch: {
    trendRequestParams() {
      this.fetchTrends()
    }
  },
  created() {
    this.fetchTrends()
  },
  methods: {
    async fetchTrends() {
      this.loading = true
      try {
        let { data } = await trialSummaryService.listTrialSummaryTrends(this.trendRequestParams)
        this.trends = data
      } finally {
        this.loading = false
      }
    },
    getTraceFromTrends(trends, status, name, color) {
      return _.merge({}, getBaseTrace(this.preSelectedGraphType, color), {
        x: this.x,
        y: _.map(trends, trend => trend[status]),
        name: name,
        visible: this.getVisibleForSelectedTable(status),
        status: status
      })
    },
    getVisibleForSelectedTable(status) {
      return _.includes(this.preSelectedTraces, status) ? true : 'legendonly'
    },
    onChange(graphType) {
      this.$emit('graph-type-change', graphType)
    },
    onRestyle(event) {
      const tracesToUpdate = _.map(event[1], (traceIndex, index) => {
        return { status: this.traces[traceIndex].status, visible: event[0].visible[index] }
      })
      let selectedTraces = _.clone(this.preSelectedTraces)
      _.forEach(tracesToUpdate, trace => {
        if (trace.visible === true && !_.includes(selectedTraces, trace.status)) {
          selectedTraces = _.concat(selectedTraces, trace.status)
        } else if (trace.visible === 'legendonly' && _.includes(selectedTraces, trace.status)) {
          selectedTraces = _.without(selectedTraces, trace.status)
        }
      })
      this.$emit('update-traces', selectedTraces)
    }
  }
}
</script>

<style lang="scss" scoped>
.trending-graph-select {
  max-width: 150px;
}
</style>
